import { Typography, Divider, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Theme from '../Themes/main.theme';
import React, { useContext, useEffect } from 'react';
import ApplicationFormSteps from '../components/FormContainers/applicationLocation/ApplicationFormSteps';
import BackButton from '../components/Buttons/BackButton';
import { useTranslation } from 'react-i18next';
import { AppleTitleNamingKeys, ILevel3 } from '../lib/generalTypes';
import { MapPoint } from '../components/Maps/types';
import { transformFormBodyToFormData } from '../lib/static/locations/formHelpers';
import { getApplicationLocationDropdownOptions, getInternalDetails, getOfficeDetails, uploadApplicationLocationForm } from '../lib/static/locations/fetchers';
import PreviewContext from '../components/context/PreviewContext';
import { useSnackbar } from 'notistack';
import { initialValues } from '../lib/static/locations/initialValues';
import UserService from '../services/UserService';
import { IntExt, Permissions } from '../lib/permissions';
import FormSkeleton from '../components/Skeletons/FormSkeleton';
import AsyncBackdropSubmit from '../components/AsyncBackdropSubmit';
import { AxiosProgressEvent } from 'axios';
import LinearWithValueLabel from '../components/LinearProgressBarWithLabel';
import i18n from '../i18n';
import { GetSortOrder } from '../lib/helperFuncs';
import { useFromAccessLogic } from '../hooks/useFromAccessLogic';

const ApplicationForm = () => {
  const navigate = useNavigate();
  const { setForm } = useContext(PreviewContext.PreviewProvider.context);
  const [level1Options, setlevel1Options] = React.useState([]);
  const [level2Options, setlevel2Options] = React.useState([]);
  const [level3Options, setLevel3Options] = React.useState<ILevel3[]>([]);
  const [onSubmitLoading, setOnSubmitLoading] = React.useState<boolean>(false);
  const [regions, setRegions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { availableForms, isLoading: isFormPublishedLoading } =
    useFromAccessLogic(AppleTitleNamingKeys.LocationRegister);
  const [officeDetails, setOfficeDetails] = React.useState<any>();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [progress, setProgress] = React.useState(0);
  const options = {
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = 0 } = progressEvent;
      const percent = Math.floor(loaded * 100 / total);

      if (percent < 100) {
        setProgress(percent);
      }
    }
  };
  const is_external = UserService.hasRole([IntExt.External]);

  document.title = is_external
    ? t('Αίτημα Καταχώρισης Τοποθεσίας')
    : t('Εσωτερικό Αίτημα Καταχώρισης Τοποθεσίας');

  const onSubmit = async (values: any, geolocation: MapPoint) => {
    setOnSubmitLoading(true);
    const formValues = {
      ...values,
      lat: geolocation?.lat
        ? String(geolocation?.lat)
        : null,
      lon: geolocation?.lng
        ? String(geolocation?.lng)
        : null
    };
    const formData = transformFormBodyToFormData(formValues);    
    const resp = await uploadApplicationLocationForm(formData, options);
    if (resp?.status === 201) {
      sessionStorage.setItem('location_id', resp?.data?.location_id.toString());
      sessionStorage.setItem('application_id', resp?.data?.application_id.toString());
      setForm({ location_id: resp?.data?.location_id, application_id: resp?.data?.application_id });
      enqueueSnackbar(t('Το αίτημα καταχώρισης ιδιοκτησίας αποθηκεύτηκε στα Πρόχειρα!'), { variant: 'success' });
      is_external
        ? navigate('/preview')
        : navigate('/admin/locations/preview');
      setOnSubmitLoading(false);
    } else {
      enqueueSnackbar(t('Το αίτημα καταχώρισης ιδιοκτησίας απέτυχε, παρακαλώ προσπαθήστε ξανά.'), { variant: 'error' });
      setOnSubmitLoading(false);

    }
  };

  const getInternalUserData = async () => {
    try {
      setIsLoading(true);
      const officeDetails = await getOfficeDetails();
      setOfficeDetails(officeDetails);
    } catch (error) {
      console.error(error);
    }
  };
  const getTaxisUserData = async () => {
    try {
      setIsLoading(true);
      const response = await getInternalDetails();
      setOfficeDetails({
        email: response.email,
        phone_number: response.phone_number,
        first_name: response.first_name,
        last_name: response.last_name
      });
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {

    if (is_external && !isFormPublishedLoading) {
      if (!availableForms.is_published) {
        enqueueSnackbar(t('Το αίτημα καταχώρισης ιδιοκτησίας δεν είναι διαθέσιμο αυτή τη στιγμή.'), { variant: 'info' });
        navigate('/location');
      }
    }

    const getParametrics = async () => {
      const { level1, level2, level3, regionsOptions } =
        await getApplicationLocationDropdownOptions();

      const level1Options_ = level1.map((option1: any) => {
        return {
          key: t(option1.key),
          value: option1.value
        };
      });

      const level2Options_ = level2.map((option2: any) => {
        return {
          key: t(option2.key),
          value: option2.value,
          parent: t(option2.parent)
        };
      }).filter((value: any) => value);

      const level3Options_ = level3.map((option3: any) => {
        return {
          ...option3,
          key: t(option3.key),
          parent: t(option3.parent)
        };
      });

      const regions = regionsOptions.map((region: any) => {
        return {
          key: t(region.key),
          value: region.value
        };
      });

      setlevel1Options(i18n.language === 'en'
        ? level1Options_.sort(GetSortOrder('key'))
        : level1Options_.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
      setlevel2Options(i18n.language === 'en'
        ? level2Options_.sort(GetSortOrder('parent'))
        : level2Options_.sort((a: any, b: any) => a.parent.localeCompare(b.parent, 'el')));
      setLevel3Options(i18n.language === 'en'
        ? level3Options_.sort(GetSortOrder('parent'))
        : level3Options_.sort((a: any, b: any) => a.parent.localeCompare(b.parent, 'el')));
      setRegions(i18n.language === 'en'
        ? regions.sort(GetSortOrder('key'))
        : regions.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
    };

    !is_external
      ? getInternalUserData()
      : getTaxisUserData();

    setIsLoading(false);

    getParametrics();
  }, [isFormPublishedLoading]);

  return (
    <>
      <AsyncBackdropSubmit isLoading={onSubmitLoading}>
        {t('Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...')}
        <LinearWithValueLabel progress={progress} />
      </AsyncBackdropSubmit>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate(-1)} />
      </Box>
      {is_external
        ? <>
          <Typography variant='h2' sx={{ my: 2 }}
            color={Theme?.labelColor?.main}>{t('Αίτημα Καταχώρισης Τοποθεσίας')}
          </Typography>
        </>
        : <Typography variant='h2' sx={{ my: 2 }}
          color={Theme?.labelColor?.main}>{t('Εσωτερικό Αίτημα Καταχώρισης Τοποθεσίας')}
        </Typography>}
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      {!isLoading && !isFormPublishedLoading ?
        <ApplicationFormSteps
          initialValues={
            is_external
              ? initialValues({
                name: officeDetails?.first_name,
                lastname: officeDetails?.last_name,
                email: officeDetails?.email,
                mobile: officeDetails?.phone_number
              })
              : initialValues({
                email: officeDetails?.email,
                mobile: officeDetails?.phone,
                ...((UserService
                  .hasRole([Permissions.PermissionInternalLocationApplicationCreateMy_org])
                  &&
                  !UserService
                    .hasRole([Permissions.PermissionInternalLocationApplicationCreateAll])
                ) && {
                  region_id: officeDetails?.region_id
                })
              })
          }
          level1Options={level1Options}
          level2Options={level2Options}
          level3Options={level3Options}
          regions={regions}
          onSubmit={onSubmit}
          isLoading={isLoading} />
        : <FormSkeleton lines={6} />}
    </>
  );
};

export default ApplicationForm;